import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Wrench, Plus, Search, Trash2, Edit2, AlertTriangle, CheckCircle,
  BarChart3, Clock, TrendingUp, X, ChevronRight, Save, Loader2,
  Truck, Settings, PauseCircle, XCircle, ArrowLeft
} from 'lucide-react';

const STATUS_BADGE = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  idle: { label: 'Idle', cls: 'bg-amber-100 text-amber-700' },
  under_repair: { label: 'Under Repair', cls: 'bg-red-100 text-red-700' },
  off_hired: { label: 'Off-Hired', cls: 'bg-slate-100 text-slate-600' },
};

const OWNERSHIP_OPTS = [
  { value: 'owned', label: 'Owned' },
  { value: 'hired', label: 'Hired' },
  { value: 'subcontractor', label: 'Subcontractor' },
];

export default function PlantEquipmentPage() {
  const { user } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('register'); // register, analytics, breakdowns
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filters, setFilters] = useState({ status: '', ownership: '', search: '' });
  const [analytics, setAnalytics] = useState(null);
  const [breakdowns, setBreakdowns] = useState([]);
  const [showBreakdownForm, setShowBreakdownForm] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', plant_type: '', make_model: '', serial_number: '',
    ownership: 'owned', supplier: '', status: 'active', project_id: '',
    assigned_area: '', operator: '', daily_cost: 0, notes: '',
  });
  const [bdForm, setBdForm] = useState({
    plant_id: '', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '',
    duration_hours: 0, cause: '', activity_affected: '', area_affected: '',
    work_stopped: false, replacement_used: false, replacement_details: '', notes: '',
  });

  useEffect(() => {
    loadProject();
    loadItems();
  }, [projectId]);

  useEffect(() => {
    loadItems();
  }, [filters.status, filters.ownership]);

  const loadProject = async () => {
    try {
      const r = await api.get(`/projects/${projectId}`);
      setProject(r.data);
    } catch (e) { console.error(e); }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      if (filters.status) params.append('status', filters.status);
      if (filters.ownership) params.append('ownership', filters.ownership);
      const r = await api.get(`/plant/register?${params}`);
      let list = r.data.items || [];
      if (filters.search) {
        const s = filters.search.toLowerCase();
        list = list.filter(i => i.name?.toLowerCase().includes(s) || i.make_model?.toLowerCase().includes(s) || i.serial_number?.toLowerCase().includes(s));
      }
      setItems(list);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadAnalytics = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/plant/analytics/${projectId}?days=30`);
      setAnalytics(r.data);
    } catch (e) { toast.error('Failed to load analytics'); }
  };

  const loadBreakdowns = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/plant/breakdowns/${projectId}`);
      setBreakdowns(r.data.breakdowns || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (view === 'analytics') loadAnalytics();
    if (view === 'breakdowns') loadBreakdowns();
  }, [view, projectId]);

  const handleSave = async () => {
    try {
      const payload = { ...form, project_id: projectId };
      if (editItem) {
        await api.put(`/plant/register/${editItem.id}`, payload);
        toast.success('Plant item updated');
      } else {
        await api.post('/plant/register', payload);
        toast.success('Plant item added');
      }
      setShowForm(false);
      setEditItem(null);
      setForm({ name: '', plant_type: '', make_model: '', serial_number: '', ownership: 'owned', supplier: '', status: 'active', project_id: '', assigned_area: '', operator: '', daily_cost: 0, notes: '' });
      loadItems();
    } catch (e) { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plant item?')) return;
    try {
      await api.delete(`/plant/register/${id}`);
      toast.success('Deleted');
      loadItems();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleBreakdownSave = async () => {
    try {
      await api.post('/plant/breakdowns', { ...bdForm, project_id: projectId });
      toast.success('Breakdown recorded');
      setShowBreakdownForm(false);
      setBdForm({ plant_id: '', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '', duration_hours: 0, cause: '', activity_affected: '', area_affected: '', work_stopped: false, replacement_used: false, replacement_details: '', notes: '' });
      loadBreakdowns();
    } catch (e) { toast.error('Save failed'); }
  };

  const startEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name || '', plant_type: item.plant_type || '', make_model: item.make_model || '',
      serial_number: item.serial_number || '', ownership: item.ownership || 'owned', supplier: item.supplier || '',
      status: item.status || 'active', project_id: item.project_id || '', assigned_area: item.assigned_area || '',
      operator: item.operator || '', daily_cost: item.daily_cost || 0, notes: item.notes || '',
    });
    setShowForm(true);
  };

  const currentProject = project;

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6" data-testid="plant-equipment-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/projects/${projectId}`)} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Wrench className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600" /> Plant & Equipment
              </h1>
              <p className="text-sm text-slate-500 mt-1">{project?.name || ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => { setEditItem(null); setForm({ name: '', plant_type: '', make_model: '', serial_number: '', ownership: 'owned', supplier: '', status: 'active', project_id: '', assigned_area: '', operator: '', daily_cost: 0, notes: '' }); setShowForm(true); }} className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-plant-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Plant
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="plant-tabs">
          {[
            { id: 'register', label: 'Register', icon: Settings },
            { id: 'breakdowns', label: 'Breakdowns', icon: AlertTriangle },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          ].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${view === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`plant-tab-${t.id}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Register View */}
        {view === 'register' && (
          <>
            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search plant..." className="pl-9 w-full sm:w-56" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="plant-search" />
              </div>
              <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="idle">Idle</SelectItem>
                  <SelectItem value="under_repair">Under Repair</SelectItem>
                  <SelectItem value="off_hired">Off-Hired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Items Table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Make/Model</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Ownership</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Operator</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr><td colSpan="7" className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                    ) : items.length === 0 ? (
                      <tr><td colSpan="7" className="text-center py-12 text-slate-400">No plant items found. Add your first plant item above.</td></tr>
                    ) : items.map(item => {
                      const st = STATUS_BADGE[item.status] || STATUS_BADGE.active;
                      return (
                        <tr key={item.id} className="hover:bg-slate-50 transition-colors" data-testid={`plant-row-${item.id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                          <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{item.plant_type}</td>
                          <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{item.make_model}</td>
                          <td className="px-4 py-3"><Badge variant="outline" className="text-xs capitalize">{item.ownership}</Badge></td>
                          <td className="px-4 py-3"><Badge className={`${st.cls} text-xs`}>{st.label}</Badge></td>
                          <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{item.operator || '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => startEdit(item)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-plant-${item.id}`}><Edit2 className="h-3.5 w-3.5 text-slate-500" /></button>
                              <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-plant-${item.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Analytics View */}
        {view === 'analytics' && analytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Plant', value: analytics.total_plant, icon: Wrench, color: 'text-cyan-600' },
                { label: 'Total Returns', value: analytics.total_returns, icon: Clock, color: 'text-blue-600' },
                { label: 'Breakdowns', value: analytics.total_breakdowns, icon: AlertTriangle, color: 'text-red-600' },
                { label: 'Period', value: `${analytics.period_days} days`, icon: TrendingUp, color: 'text-emerald-600' },
              ].map((s, i) => (
                <Card key={i}><CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3"><s.icon className={`h-6 w-6 sm:h-8 sm:w-8 ${s.color}`} /><div><p className="text-[10px] sm:text-xs text-slate-500">{s.label}</p><p className="text-base sm:text-xl font-bold text-slate-900">{s.value}</p></div></CardContent></Card>
              ))}
            </div>
            {analytics.plant_stats?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Plant Utilisation</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.plant_stats.map((ps, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                        <span className="text-xs sm:text-sm font-medium text-slate-700 sm:w-40 truncate">{ps.name}</span>
                        <div className="flex items-center gap-2 flex-1">
                          <div className="flex-1 bg-slate-100 rounded-full h-3 sm:h-4 relative overflow-hidden">
                            <div className="bg-cyan-500 h-full rounded-full transition-all" style={{ width: `${Math.min(ps.utilisation_pct, 100)}%` }} />
                          </div>
                          <span className="text-xs sm:text-sm font-semibold text-slate-700 w-10 sm:w-14 text-right">{ps.utilisation_pct}%</span>
                          {ps.breakdown_count > 0 && <Badge className="bg-red-100 text-red-700 text-[10px] hidden sm:inline-flex">{ps.breakdown_count} breakdowns</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Breakdowns View */}
        {view === 'breakdowns' && (
          <>
            <div className="flex justify-end">
              <Button onClick={() => setShowBreakdownForm(true)} variant="destructive" className="gap-1" data-testid="add-breakdown-btn">
                <AlertTriangle className="h-4 w-4" /> Log Breakdown
              </Button>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Plant</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Cause</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Duration</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Work Stopped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {breakdowns.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-12 text-slate-400">No breakdowns recorded</td></tr>
                  ) : breakdowns.map(bd => (
                    <tr key={bd.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{bd.date}</td>
                      <td className="px-4 py-3 font-medium">{items.find(i => i.id === bd.plant_id)?.name || bd.plant_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3">{bd.cause}</td>
                      <td className="px-4 py-3">{bd.duration_hours}h</td>
                      <td className="px-4 py-3">{bd.work_stopped ? <Badge className="bg-red-100 text-red-700 text-xs">Yes</Badge> : <Badge variant="outline" className="text-xs">No</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}

        {/* Add/Edit Plant Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="plant-form-modal">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">{editItem ? 'Edit Plant Item' : 'Add Plant Item'}</h2>
                <button onClick={() => { setShowForm(false); setEditItem(null); }} className="p-1 rounded hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-3">
                <div><label className="text-xs font-medium text-slate-600">Name *</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. CAT 320 Excavator" data-testid="plant-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Type</label><Input value={form.plant_type} onChange={e => setForm(f => ({ ...f, plant_type: e.target.value }))} placeholder="Excavator, Crane..." /></div>
                  <div><label className="text-xs font-medium text-slate-600">Make/Model</label><Input value={form.make_model} onChange={e => setForm(f => ({ ...f, make_model: e.target.value }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Serial No.</label><Input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Supplier</label><Input value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Ownership</label>
                    <Select value={form.ownership} onValueChange={v => setForm(f => ({ ...f, ownership: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{OWNERSHIP_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-xs font-medium text-slate-600">Status</label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="idle">Idle</SelectItem>
                        <SelectItem value="under_repair">Under Repair</SelectItem>
                        <SelectItem value="off_hired">Off-Hired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Assigned Area</label><Input value={form.assigned_area} onChange={e => setForm(f => ({ ...f, assigned_area: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Operator</label><Input value={form.operator} onChange={e => setForm(f => ({ ...f, operator: e.target.value }))} /></div>
                </div>
                <div><label className="text-xs font-medium text-slate-600">Daily Cost (R)</label><Input type="number" value={form.daily_cost} onChange={e => setForm(f => ({ ...f, daily_cost: parseFloat(e.target.value) || 0 }))} /></div>
                <div><label className="text-xs font-medium text-slate-600">Notes</label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="save-plant-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
                  <Button onClick={() => { setShowForm(false); setEditItem(null); }} variant="outline" className="flex-1">Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Breakdown Form Modal */}
        {showBreakdownForm && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="breakdown-form-modal">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Log Breakdown</h2>
                <button onClick={() => setShowBreakdownForm(false)} className="p-1 rounded hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-3">
                <div><label className="text-xs font-medium text-slate-600">Plant Item *</label>
                  <Select value={bdForm.plant_id} onValueChange={v => setBdForm(f => ({ ...f, plant_id: v }))}>
                    <SelectTrigger data-testid="bd-plant-select"><SelectValue placeholder="Select plant" /></SelectTrigger>
                    <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs font-medium text-slate-600">Date</label><Input type="date" value={bdForm.date} onChange={e => setBdForm(f => ({ ...f, date: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Start Time</label><Input type="time" value={bdForm.start_time} onChange={e => setBdForm(f => ({ ...f, start_time: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium text-slate-600">End Time</label><Input type="time" value={bdForm.end_time} onChange={e => setBdForm(f => ({ ...f, end_time: e.target.value }))} /></div>
                </div>
                <div><label className="text-xs font-medium text-slate-600">Duration (hours)</label><Input type="number" step="0.5" value={bdForm.duration_hours} onChange={e => setBdForm(f => ({ ...f, duration_hours: parseFloat(e.target.value) || 0 }))} /></div>
                <div><label className="text-xs font-medium text-slate-600">Cause</label><Input value={bdForm.cause} onChange={e => setBdForm(f => ({ ...f, cause: e.target.value }))} placeholder="Hydraulic failure, engine..." /></div>
                <div><label className="text-xs font-medium text-slate-600">Activity Affected</label><Input value={bdForm.activity_affected} onChange={e => setBdForm(f => ({ ...f, activity_affected: e.target.value }))} /></div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bdForm.work_stopped} onChange={e => setBdForm(f => ({ ...f, work_stopped: e.target.checked }))} className="rounded" /> Work Stopped</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bdForm.replacement_used} onChange={e => setBdForm(f => ({ ...f, replacement_used: e.target.checked }))} className="rounded" /> Replacement Used</label>
                </div>
                <div><label className="text-xs font-medium text-slate-600">Notes</label><Textarea value={bdForm.notes} onChange={e => setBdForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
                <Button onClick={handleBreakdownSave} className="w-full bg-red-600 hover:bg-red-700" data-testid="save-breakdown-btn"><Save className="h-4 w-4 mr-1" /> Save Breakdown</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
